import {AfterViewInit, Component, inject, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {HubspotService, SyncHistoryQueryParams} from '../../../services/enrich/hubspot.service';
import {SyncHistory} from '../../../models/hubspot.model';
import {ClientService} from '../../../services/auth/client.service';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {CommonModule} from '@angular/common';
import {MaterialModule} from '../../../material.module';
import {Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';

@Component({
  selector: 'app-enrich-sync-history',
  templateUrl: './sync-history.component.html',
  styleUrls: ['./sync-history.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, BreadcrumbsComponent]
})
export class EnrichSyncHistoryComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private hubspotService = inject(HubspotService);
  private clientService = inject(ClientService);
  private clientLoadingService = inject(ClientLoadingService);
  private router = inject(Router);
  private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

  readonly dataSource = new MatTableDataSource<SyncHistory>([]);
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  filterForm: FormGroup = this.fb.group({
    companyName: [''],
    status: [''],
    syncType: [''],
    startDate: [null],
    endDate: [null]
  });

  isLoading = false;
  error: string | null = null;

  readonly displayedColumns: string[] = ['companyName', 'status', 'syncType', 'startedAt', 'completedAt'];

  readonly breadcrumbs = [
    {label: 'Shine Enrich', route: '/enrich/setup'},
    {label: 'Company data', route: '/enrich/companies'},
    {label: 'Sync history'}
  ];

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.pageSize = 10;
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.error = null;

    this.clientLoadingService.ensureClientSelected().subscribe({
      next: () => {
        this.loadSyncHistory();
      },
      error: err => {
        this.enrichDiagnostics.reportError('enrich.sync-history.client-selection', err);
        this.error = 'No client selected. Please select a client from the dropdown.';
        this.isLoading = false;
      }
    });
  }

  refresh(): void {
    this.loadSyncHistory();
  }

  loadSyncHistory(): void {
    this.isLoading = true;
    this.error = null;

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    const params = this.getFilterParams();

    this.hubspotService.getSyncHistory(clientId, params).pipe(
      catchError(error => {
        this.enrichDiagnostics.reportError('enrich.sync-history.load', error);
        this.error = 'Failed to load sync history';
        return of([]);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe(history => {
      this.dataSource.data = this.applyLocalFilters(history ?? [], params);
      this.paginator?.firstPage();
    });
  }

  openDetails(syncHistory: SyncHistory): void {
    this.router.navigate(['/enrich/sync-history', syncHistory.id], {state: {syncHistory}});
  }

  getFilterParams(): SyncHistoryQueryParams {
    const formValue = this.filterForm.value;
    const params: SyncHistoryQueryParams = {};

    if (typeof formValue.companyName === 'string' && formValue.companyName.trim()) {
      params.companyName = formValue.companyName.trim();
    }
    if (formValue.status) {
      params.status = formValue.status;
    }
    if (formValue.syncType) {
      params.syncType = formValue.syncType;
    }
    if (formValue.startDate instanceof Date) {
      params.startDate = formValue.startDate.toISOString();
    } else if (formValue.startDate) {
      params.startDate = formValue.startDate;
    }
    if (formValue.endDate instanceof Date) {
      params.endDate = formValue.endDate.toISOString();
    } else if (formValue.endDate) {
      params.endDate = formValue.endDate;
    }

    return params;
  }

  resetFilters(): void {
    this.filterForm.reset();
    this.loadSyncHistory();
  }

  applyFilters(): void {
    this.loadSyncHistory();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'success':
        return 'status-success';
      case 'failed':
        return 'status-failed';
      case 'partial':
        return 'status-partial';
      case 'in_progress':
        return 'status-in-progress';
      default:
        return '';
    }
  }

  statusIcon(status: SyncHistory['status']): string {
    switch (status) {
      case 'success':
        return 'check_circle';
      case 'failed':
        return 'cancel';
      case 'partial':
        return 'warning';
      case 'in_progress':
        return 'schedule';
    }
  }

  statusLabel(status: SyncHistory['status']): string {
    switch (status) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'partial':
        return 'Partial';
      case 'in_progress':
        return 'In progress';
    }
  }

  formatSyncType(syncType: SyncHistory['syncType']): string {
    switch (syncType) {
      case 'manual':
        return 'Manual';
      case 'scheduled':
        return 'Scheduled';
      case 'filter':
        return 'Filter';
      case 'individual':
        return 'Individual';
      case 'daily':
        return 'Daily';
      case 'import':
        return 'Import';
      default:
        return syncType;
    }
  }

  private applyLocalFilters(history: SyncHistory[], params: SyncHistoryQueryParams): SyncHistory[] {
    if (!params.companyName) {
      return history;
    }

    const companyName = params.companyName.toLocaleLowerCase();
    return history.filter(item => (item.companyName ?? 'All companies').toLocaleLowerCase().includes(companyName));
  }

  get hasActiveFilters(): boolean {
    const value = this.filterForm.value as {
      companyName?: string;
      status?: string;
      syncType?: string;
      startDate?: unknown;
      endDate?: unknown;
    };

    return !!(
      value.companyName ||
      value.status ||
      value.syncType ||
      value.startDate ||
      value.endDate
    );
  }
}
